/**
 * Dev-only mock users for previewing the UI as any role without a real
 * backend session.
 *
 * Turn it on by setting `VITE_DEV_MOCK_ROLE` in `.env.local` to a UserRole
 * value, then restart the dev server:
 *
 *   VITE_DEV_MOCK_ROLE=SYSTEM_ADMIN        # AMLiQ super admin (sees everything)
 *   VITE_DEV_MOCK_ROLE=ADMIN_USER          # account admin
 *   VITE_DEV_MOCK_ROLE=COMPLIANCE_OFFICER  # compliance officer
 *   VITE_DEV_MOCK_ROLE=STANDARD_USER       # standard user
 *
 * On bootstrap the app signs in as a fake user with that role — no
 * `/auth/login` or `/auth/me` call. Ignored entirely in production builds.
 */
import type { AuthTokens, AuthUser, BackendRoleName, TenantDetail } from '../types'
import { env } from '@/config/env'
import { ALL_ROLES, ROLE_LABELS, UserRole } from '@/lib/rbac'

/** App role → the backend role name(s) a mock user carries within its tenant. */
const MOCK_ROLE_NAMES: Record<UserRole, Array<BackendRoleName>> = {
  [UserRole.SystemAdmin]: [],
  [UserRole.AdminUser]: ['Admin'],
  [UserRole.ComplianceOfficer]: ['Compliance Officer'],
  [UserRole.StandardUser]: ['Standard User'],
}

/**
 * Fake tokens used ONLY as a fallback when no real token is persisted. If a
 * real access token exists in localStorage, mock mode keeps it (so live API
 * calls still work) and these are not used — see `useSessionBootstrap`.
 */
export const MOCK_TOKENS: AuthTokens = {
  accessToken: 'dev-mock-access-token',
  refreshToken: 'dev-mock-refresh-token',
}

/** Build a mock authenticated user for the given role. */
export function makeMockUser(role: UserRole): AuthUser {
  return {
    id: `mock-${role.toLowerCase()}`,
    email: `${role.toLowerCase()}@amliq.dev`,
    fullName: `${ROLE_LABELS[role]} (Mock)`,
    role,
    roleNames: MOCK_ROLE_NAMES[role],
    // Super admin is platform-level (no tenant); the rest get a fake account.
    accountId: role === UserRole.SystemAdmin ? null : 'mock-account-001',
    // Dev default: a real-estate agency, so the New Check form shows the full
    // (property + price) flow. Change to preview other sectors.
    accountSector: role === UserRole.SystemAdmin ? null : 'Real estate agency',
  }
}

/**
 * Build the mock tenant detail for a mock user. Dev default: a reporting group
 * with a few branches, so the New Check form shows the entity selector. The
 * super admin has no tenant. Set `reportingGroup: false` to preview the
 * single-branch flow.
 */
export function makeMockTenant(role: UserRole): TenantDetail | null {
  if (role === UserRole.SystemAdmin) return null
  return {
    id: 'mock-account-001',
    name: 'mock-agency',
    displayName: 'Mock Agency',
    companyAcn: '004121060',
    sector: { id: 'mock-sector-001', name: 'Real estate agency' },
    status: 'active',
    reportingGroup: true,
    ssoLoginEnabled: true,
    mfaLoginEnabled: true,
    branchEntities: [
      { id: 'mock-branch-001', name: 'Brighton', isLead: true },
      { id: 'mock-branch-002', name: 'Mentone', isLead: false },
      { id: 'mock-branch-003', name: 'South Yarra', isLead: false },
    ],
  }
}

function isUserRole(value: string): value is UserRole {
  return (ALL_ROLES as ReadonlyArray<string>).includes(value)
}

/**
 * The configured dev mock role, or `null` when mock mode is off, the value
 * is invalid, or we're running a production build.
 */
export function resolveMockRole(): UserRole | null {
  if (!env.isDev) return null
  const raw = env.devMockRole
  if (!raw) return null
  if (!isUserRole(raw)) {
    console.warn(
      `[mock-user] Ignoring invalid VITE_DEV_MOCK_ROLE="${raw}". ` +
        `Expected one of: ${ALL_ROLES.join(', ')}`,
    )
    return null
  }
  return raw
}
